import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Newspaper, ChevronRight } from 'lucide-react';

const BACKEND_URL = '';

/**
 * LatestNewsPanel — compact 3-article news strip for sidebars/right-rails.
 * Pulls from the same /api/news endpoint the Progress dashboard uses so the
 * content stays consistent across surfaces. Click-through navigates to the
 * routed article page when the item has a news_id; falls back to the
 * source URL for external (RSS) items without a copy in our DB.
 *
 * Props:
 *   - limit (default 3) — number of articles to show
 *   - testid (optional) — for E2E selectors
 *   - className (optional) — wrapper class overrides
 */
export default function LatestNewsPanel({ limit = 3, testid = 'latest-news-panel', className = '' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/news?limit=${limit}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.items || data?.news || []);
        setItems(list.slice(0, limit));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (!loading && items.length === 0) return null;

  return (
    <Card className={`p-4 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-amber-600" />
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Latest News</h3>
        </div>
        <Link to="/news"
          className="text-[11px] text-brand-navy hover:underline inline-flex items-center gap-0.5"
          data-testid={`${testid}-view-all`}>
          View all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="flex gap-2 animate-pulse">
              <div className="w-14 h-14 bg-slate-100 rounded flex-shrink-0" />
              <div className="flex-1 space-y-1.5 py-1">
                <div className="h-2.5 bg-slate-100 rounded w-3/4" />
                <div className="h-2 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const externalLink = item.external_url || item.link;
            const openInline = !!item.news_id;
            const handleClick = (e) => {
              e.preventDefault();
              if (openInline) { navigate(`/news/${item.news_id}`); return; }
              if (externalLink) window.open(externalLink, '_blank', 'noopener,noreferrer');
            };
            return (
              <button
                key={item.news_id || item.link}
                onClick={handleClick}
                className="flex gap-2.5 items-start w-full text-left group"
                data-testid={`${testid}-item-${item.news_id || 'ext'}`}
              >
                <div className="w-14 h-14 bg-slate-100 rounded flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {item.image ? (
                    <img src={item.image} alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <Newspaper className="h-5 w-5 text-amber-600/40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug group-hover:text-brand-navy transition-colors">
                    {item.title}
                  </p>
                  {item.source && (
                    <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide truncate">{item.source}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
